import React, { useState } from "react";
import CommandePage from "../../core/commande/CommandePage";
import PatientForm from "../../components/PatientForm";
import ValiderCommandeModal from "../../components/ValiderCommandeModal";
import { API } from "../../services/api";

const CONTEXT = {
    filters: { validationView: "true" },
    releasedField: "managementValidated",
    lockStatut: true,
    allowWorkflowActionsWhenLocked: true,
    showStatut: true,
    showListFilters: false,
    medicamentStrategy: {
        showDestination: true,
        showDosage: false,
        showSiBesoin: true,
        showClassificationMedicament: true,
        ROBOT:      { showRobotFields: false, showEtiquetageFields: false },
        ETIQUETAGE: { showRobotFields: false, showEtiquetageFields: false },
    },
};

function toDateTime(date, time) {
    if (!time) return null;
    const d = date || new Date().toISOString().split("T")[0];
    return `${d}T${time}:00`;
}

function patientToPayload(patient) {
    return {
        commandePatientId: patient.commandePatientId ?? null,
        id: patient.id ?? null,
        nom: patient.nom,
        prenom: patient.prenom,
        dateNaissance: patient.dateNaissance,
        classification: patient.classification || "UNKNOWN",
        commentaryProductionRobot: patient.commentaryProductionRobot ?? null,
        commentaryProductionEtiquetage: patient.commentaryProductionEtiquetage ?? null,
        pharmaceuticalValidated: patient.pharmaceuticalValidated ?? false,
        identityResolution: patient._identityResolution ?? null,
        identityCandidateId: patient._identityCandidateId ?? null,
        produits: (patient.produits || []).map(prod => ({
            id: prod.id ?? null,
            nom: prod.nom ?? "",
            dosage: prod.dosage ?? "",
            posologie: prod.posologie ?? "",
            quantite: prod.quantite ?? 1,
            destination: prod.destination ?? "ROBOT",
            statut: prod.statut ?? null,
            siBesoin: prod.siBesoin ?? false,
            classificationMedicament: prod.classificationMedicament ?? null,
            deliveryDate: prod.deliveryDate ?? null,
            billingHour: prod.billingHour ?? toDateTime(prod.deliveryDate, prod.billingHourTime),
            billingValidated: prod.billingValidated ?? false,
            billingUsername: prod.billingUsername ?? null,
            planificationValidated: prod.planificationValidated ?? null,
            planificationValidatedAt: prod.planificationValidatedAt ?? null,
            planificationValidatedBy: prod.planificationValidatedBy ?? null,
            commentaryPlanificationRobot: prod.commentaryPlanificationRobot ?? null,
            commentaryPlanificationEtiquetage: prod.commentaryPlanificationEtiquetage ?? null,
            ehpadWarnedIfOutOfStock: prod.ehpadWarnedIfOutOfStock ?? false,
            pharmaceuticalValidated: prod.pharmaceuticalValidated ?? false,
        })),
    };
}

function normalizeText(value) {
    return String(value || "").trim().toLowerCase();
}

function patientSignature(patient) {
    return {
        clientKey: patient?._clientKey ?? null,
        commandePatientId: patient?.commandePatientId ?? null,
        id: patient?.id ?? null,
        nom: normalizeText(patient?.nom),
        prenom: normalizeText(patient?.prenom),
        dateNaissance: patient?.dateNaissance ?? null,
    };
}

function sameIdentity(patient, signature) {
    return normalizeText(patient?.nom) === signature.nom
        && normalizeText(patient?.prenom) === signature.prenom
        && (patient?.dateNaissance ?? null) === signature.dateNaissance;
}

function findFreshPatient(patients, signature) {
    const list = patients || [];
    if (signature.commandePatientId != null) {
        const byCommandePatientId = list.find(patient => patient.commandePatientId === signature.commandePatientId);
        if (byCommandePatientId) return byCommandePatientId;
    }
    if (signature.id != null) {
        const byPatientId = list.find(patient => patient.id === signature.id && sameIdentity(patient, signature));
        if (byPatientId) return byPatientId;
    }
    if (signature.clientKey != null) {
        const byClientKey = list.find(patient => patient._clientKey === signature.clientKey);
        if (byClientKey) return byClientKey;
    }
    return list.find(patient => sameIdentity(patient, signature)) || null;
}

function sortPharmaPatients(patients) {
    return [...(patients || [])].sort((a, b) =>
        Number(!!a.pharmaceuticalValidated) - Number(!!b.pharmaceuticalValidated)
    );
}

function patientKey(patient) {
    return patient?._clientKey || patient?.commandePatientId || patient?.id || null;
}

function mergeDisplayedPatientsIntoDraft(previousPatients, displayedPatients) {
    const incoming = displayedPatients || [];
    const incomingByKey = new Map(
        incoming
            .map(patient => [patientKey(patient), patient])
            .filter(([key]) => key != null)
    );
    const usedKeys = new Set();
    const merged = (previousPatients || [])
        .map(patient => {
            const key = patientKey(patient);
            if (key == null || !incomingByKey.has(key)) return patient;
            usedKeys.add(key);
            const incomingPatient = incomingByKey.get(key);
            const incomingProducts = new Map((incomingPatient.produits || []).map(product => [product.id, product]));
            const mergedProducts = (patient.produits || []).map(product => incomingProducts.get(product.id) || product);
            (incomingPatient.produits || []).forEach(product => {
                if (product.id == null || !(patient.produits || []).some(existing => existing.id === product.id)) {
                    mergedProducts.push(product);
                }
            });
            return { ...patient, ...incomingPatient, produits: mergedProducts };
        })
        .filter(Boolean);

    incoming.forEach(patient => {
        const key = patientKey(patient);
        if (key == null || !usedKeys.has(key)) {
            merged.push(patient);
        }
    });

    return merged;
}

function mergeValidatedCommandeIntoDraft(previousDraft, validatedCommande) {
    if (!previousDraft || !validatedCommande) return previousDraft;
    const validatedPatients = validatedCommande.patients || [];
    return {
        ...previousDraft,
        statut: validatedCommande.statut ?? previousDraft.statut,
        patients: (previousDraft.patients || []).map(patient => {
            const freshPatient = findFreshPatient(validatedPatients, patientSignature(patient));
            const freshProducts = new Map((freshPatient?.produits || []).map(product => [product.id, product]));
            return freshPatient
                ? {
                    ...patient,
                    ...freshPatient,
                    managementValidated: freshPatient.managementValidated ?? patient.managementValidated,
                    produits: (patient.produits || []).map(product => ({
                        ...product,
                        ...(freshProducts.get(product.id) || {}),
                        managementValidated: freshProducts.get(product.id)?.managementValidated
                            ?? product.managementValidated,
                    })),
                    _clientKey: patient._clientKey,
                    _createdInCurrentDraft: patient._createdInCurrentDraft,
                }
                : patient;
        }),
    };
}

function markValidatedPatientInDraft(previousDraft, patientToValidate, validatedCommande) {
    const mergedDraft = mergeValidatedCommandeIntoDraft(previousDraft, validatedCommande);
    if (!mergedDraft) return mergedDraft;
    const validatedIds = new Set(
        (validatedCommande?.patients || [])
            .filter(patient => patient.pharmaceuticalValidated)
            .map(patient => patient.commandePatientId)
            .filter(id => id != null)
    );
    return {
        ...mergedDraft,
        patients: (mergedDraft.patients || []).map(patient => {
            const sameCommandePatient = patientToValidate?.commandePatientId != null
                && patient.commandePatientId === patientToValidate.commandePatientId;
            const samePatient = patientToValidate?.id != null
                && patient.id === patientToValidate.id
                && sameIdentity(patient, patientSignature(patientToValidate));
            return sameCommandePatient || samePatient || validatedIds.has(patient.commandePatientId)
                ? { ...patient, pharmaceuticalValidated: true }
                : patient;
        }),
    };
}

function productValidationLabel(validating, selectionActive, selectedProductCount) {
    if (validating) return "Validation...";
    if (selectionActive) return `Valider les ${selectedProductCount} produits`;
    return "Valider des produits";
}

function ProductValidationAction({
    draft,
    setWorkflowDraft,
    save,
    isDirty,
    isValide,
    productSelection,
    setProductSelection,
    selectedProductIds,
    selectionActive,
    markLocalValidated,
    lockedByUser,
}) {
    const [validationError, setValidationError] = useState(null);
    if (isValide) return null;
    const selectedProductCount = selectedProductIds.size;
    const selectionIsEmpty = selectionActive && selectedProductCount === 0;

    async function saveChangesIfNeeded() {
        if (!isDirty || lockedByUser) return true;
        return save();
    }

    async function validateSelectedProducts() {
        setProductSelection(previous => ({ ...previous, validating: true }));
        setValidationError(null);
        try {
            if (!await saveChangesIfNeeded()) return;
            const response = await API.post(
                `/commandes/${draft.id}/valider-produits`,
                { medicamentIds: [...selectedProductIds] },
                { noRedirectOn401: true }
            );
            setWorkflowDraft(previous => mergeValidatedCommandeIntoDraft(previous, response.data));
            const newlyValidated = (response.data?.patients || [])
                .filter(patient => patient.pharmaceuticalValidated)
                .map(patient => patient.commandePatientId)
                .filter(id => id != null);
            markLocalValidated(draft.id, newlyValidated);
            setProductSelection({ commandeId: draft.id, active: false, ids: new Set(), validating: false });
        } catch (error) {
            setValidationError(
                error?.response?.data?.message
                || error?.response?.data?.detail
                || "La validation des produits a échoué. Réessayez."
            );
        } finally {
            setProductSelection(previous => previous.commandeId === draft.id
                ? { ...previous, validating: false }
                : previous);
        }
    }

    async function handleValidationClick() {
        if (selectionActive) {
            await validateSelectedProducts();
            return;
        }
        if (isDirty && !lockedByUser && !await save()) return;
        setProductSelection({ commandeId: draft.id, active: true, ids: new Set(), validating: false });
    }

    return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
            {validationError && (
                <span role="alert" style={{ color: "#b91c1c", fontSize: "13px", fontWeight: 600 }}>
                    {validationError}
                </span>
            )}
            {selectionActive && (
                <button
                    type="button"
                    onClick={() => setProductSelection({ commandeId: draft.id, active: false, ids: new Set(), validating: false })}
                    disabled={productSelection.validating}
                    style={{ padding: "9px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", background: "white", color: "#475569", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}
                >
                    Annuler
                </button>
            )}
            <button
                type="button"
                disabled={selectionActive && (selectedProductCount === 0 || productSelection.validating)}
                onClick={handleValidationClick}
                style={{
                    padding: "10px 16px",
                    borderRadius: "8px",
                    border: "none",
                    background: selectionIsEmpty ? "#cbd5e1" : "#0C3B3C",
                    color: "white",
                    cursor: selectionIsEmpty ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: 700,
                }}
            >
                {productValidationLabel(productSelection.validating, selectionActive, selectedProductCount)}
            </button>
        </div>
    );
}

function ValidationPatientForm({
    draft,
    setDraft,
    setWorkflowDraft,
    save,
    isDirty,
    commande,
    localValidated,
    productSelection,
    setProductSelection,
    toggleProductSelection,
    markLocalValidated,
    lockedByUser,
}) {
    const isValide = draft?.statut === "VALIDE" || commande?.statut === "VALIDE";
    const selectionActive = productSelection.commandeId === draft?.id && productSelection.active;
    const selectedProductIds = productSelection.commandeId === draft?.id
        ? productSelection.ids
        : new Set();
    const localIds = localValidated.commandeId === draft?.id ? localValidated.ids : new Set();
    const pharmaPatients = (draft?.patients || []).map(patient =>
        ((isValide && patient.commandePatientId) || localIds.has(patient.commandePatientId))
            ? { ...patient, pharmaceuticalValidated: true }
            : patient
    ).map(patient => ({
        ...patient,
        produits: (patient.produits || []).filter(product =>
            product.managementValidated || product.id == null
        ),
    })).filter(patient =>
        patient.managementValidated || patient.commandePatientId == null || patient.produits.length > 0
    );
    const displayPatients = isValide ? pharmaPatients : sortPharmaPatients(pharmaPatients);

    async function validatePatient(patient) {
        let patientForValidation = patient;
        if (isDirty && !lockedByUser) {
            const signature = patientSignature(patient);
            if (!await save()) return false;
            const freshResponse = await API.get(
                `/commandes/${draft.id}`,
                { params: { _ts: Date.now() }, headers: { "Cache-Control": "no-cache" } }
            );
            const freshPatient = findFreshPatient(freshResponse.data?.patients, signature);
            if (!freshPatient) return false;
            patientForValidation = freshPatient;
        }
        const response = await API.post(
            `/commandes/${draft.id}/valider`,
            {
                id: draft.id,
                ehpad: draft.ehpadId ? { id: draft.ehpadId } : null,
                patients: [patientToPayload(patientForValidation)],
            },
            { noRedirectOn401: true }
        );
        setWorkflowDraft(previous => markValidatedPatientInDraft(previous, patientForValidation, response.data));
        const newlyValidated = (response.data?.patients || [])
            .filter(patientItem => patientItem.pharmaceuticalValidated)
            .map(patientItem => patientItem.commandePatientId);
        markLocalValidated(draft.id, newlyValidated);
        return true;
    }

    return (
        <PatientForm
            patients={displayPatients}
            ehpadId={draft?.ehpadId}
            medicamentStrategy={CONTEXT.medicamentStrategy}
            onChange={patients => setDraft(previous => ({
                ...previous,
                patients: mergeDisplayedPatientsIntoDraft(previous?.patients || [], patients),
            }))}
            onSave={save}
            isDirty={isDirty}
            productSelectionMode={selectionActive}
            selectedProductIds={selectedProductIds}
            onToggleProductSelection={medicamentId => toggleProductSelection(draft.id, medicamentId)}
            productValidationAction={(
                <ProductValidationAction
                    draft={draft}
                    setWorkflowDraft={setWorkflowDraft}
                    save={save}
                    isDirty={isDirty}
                    isValide={isValide}
                    productSelection={productSelection}
                    setProductSelection={setProductSelection}
                    selectedProductIds={selectedProductIds}
                    selectionActive={selectionActive}
                    markLocalValidated={markLocalValidated}
                    lockedByUser={lockedByUser}
                />
            )}
            onValidatePatient={validatePatient}
            canValidatePatient={patient => !!patient.managementValidated}
        />
    );
}

export default function ValidationPharma() {
    const [pendingAction, setPendingAction] = useState(null);
    const [localValidated, setLocalValidated] = useState({ commandeId: null, ids: new Set() });
    const [productSelection, setProductSelection] = useState({ commandeId: null, active: false, ids: new Set(), validating: false });

    function markLocalValidated(commandeId, newIds) {
        setLocalValidated(prev => {
            const base = prev.commandeId === commandeId ? prev.ids : new Set();
            return { commandeId, ids: new Set([...base, ...newIds]) };
        });
    }

    function toggleProductSelection(commandeId, medicamentId) {
        if (medicamentId == null) return;
        setProductSelection(prev => {
            const ids = prev.commandeId === commandeId ? new Set(prev.ids) : new Set();
            if (ids.has(medicamentId)) ids.delete(medicamentId);
            else ids.add(medicamentId);
            return { commandeId, active: true, ids, validating: false };
        });
    }

    const handleModalClose = (success) => {
        if (success && pendingAction) {
            pendingAction.goToList();
            pendingAction.refresh();
        }
        setPendingAction(null);
    };

    return (
        <>
        {pendingAction && (
            <ValiderCommandeModal
                commande={pendingAction.commande}
                onClose={handleModalClose}
            />
        )}
        <CommandePage
            context={CONTEXT}
            renderDetailActions={({ commande, goToList, refresh, saveDraft, isDirty, lockedByUser }) => {
                if (commande.statut !== "A_VALIDER" || (commande.managementStatut ?? "VALIDE") !== "VALIDE") return null;
                return (
                    <button
                        onClick={async () => {
                            if (isDirty && !lockedByUser) {
                                const saved = await saveDraft();
                                if (!saved) return;
                            }
                            setPendingAction({ commande, goToList, refresh });
                        }}
                        style={{
                            padding: "5px 16px",
                            borderRadius: "7px",
                            border: "none",
                            background: "#16a34a",
                            color: "white",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "flex", alignItems: "center", gap: "6px",
                            transition: "background 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#15803d"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "#16a34a"; }}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Valider
                    </button>
                );
            }}
        >
            {({ draft, setDraft, setWorkflowDraft, save, isDirty, commande, lockedByUser }) => (
                <ValidationPatientForm
                    draft={draft}
                    setDraft={setDraft}
                    setWorkflowDraft={setWorkflowDraft}
                    save={save}
                    isDirty={isDirty}
                    commande={commande}
                    localValidated={localValidated}
                    productSelection={productSelection}
                    setProductSelection={setProductSelection}
                    toggleProductSelection={toggleProductSelection}
                    markLocalValidated={markLocalValidated}
                    lockedByUser={lockedByUser}
                />
            )}
        </CommandePage>
        </>
    );
}
